import { useRef, useState } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Check, ShieldCheck } from "lucide-react";
import { SplitReveal, FadeUp } from "../components/SplitReveal";
import { useLanguage } from "../lib/i18n";
import { usePrefersReducedMotion } from "../lib/useReducedMotion";

// GARANTİ — 3D holographic warranty card that tilts and shines with the mouse.
export function Warranty() {
  const { t } = useLanguage();
  const reduced = usePrefersReducedMotion();
  const ref = useRef(null);
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const rx = useSpring(useTransform(my, [0, 1], [12, -12]), { stiffness: 150, damping: 18 });
  const ry = useSpring(useTransform(mx, [0, 1], [-12, 12]), { stiffness: 150, damping: 18 });
  const [shine, setShine] = useState({ x: 50, y: 50 });

  const onMove = (e) => {
    if (reduced || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width;
    const py = (e.clientY - r.top) / r.height;
    mx.set(px);
    my.set(py);
    setShine({ x: px * 100, y: py * 100 });
  };

  return (
    <section
      id="warranty"
      ref={ref}
      onMouseMove={onMove}
      className="relative bg-[#08090B] px-6 py-24 md:px-12 md:py-40"
      data-testid="warranty-section"
    >
      <div className="mx-auto grid max-w-[1600px] items-center gap-16 lg:grid-cols-2">
        <div className="flex justify-center [perspective:1200px]">
          <motion.div
            style={reduced ? {} : { rotateX: rx, rotateY: ry }}
            data-cursor
            data-cursor-label="DÖNDÜR"
            data-testid="warranty-card"
            className="relative aspect-[1.586/1] w-full max-w-md overflow-hidden rounded-3xl border border-white/15 bg-gradient-to-br from-[#15171d] to-[#0a0b0e] p-8 shadow-2xl"
          >
            {/* holographic shine */}
            <div
              className="pointer-events-none absolute inset-0"
              style={{
                background: `radial-gradient(circle at ${shine.x}% ${shine.y}%, rgba(255,148,85,0.35), transparent 45%), linear-gradient(120deg, transparent 30%, rgba(255,255,255,0.08) 50%, transparent 70%)`,
              }}
            />
            <div className="relative flex h-full flex-col justify-between">
              <div className="flex items-start justify-between">
                <ShieldCheck className="text-hars-orange" size={40} />
                <span className="heading text-lg text-white/40">HARSPRO</span>
              </div>
              <div>
                <p className="heading text-5xl text-white md:text-6xl">{t.warranty.cardTitle}</p>
                <p className="mt-2 font-inter text-sm tracking-[0.3em] text-hars-orange">{t.warranty.cardSub}</p>
              </div>
            </div>
          </motion.div>
        </div>

        <div>
          <FadeUp>
            <p className="heading mb-4 text-sm not-italic tracking-[0.3em] text-hars-orange">{t.warranty.kicker}</p>
          </FadeUp>
          <SplitReveal text={t.warranty.title} className="heading text-5xl text-white md:text-6xl" />
          <ul className="mt-8 space-y-4">
            {t.warranty.points.map((p, i) => (
              <FadeUp key={i} delay={i * 0.08}>
                <li className="flex items-center gap-3">
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-hars-orange/15 text-hars-orange">
                    <Check size={16} />
                  </span>
                  <span className="font-inter text-base text-white/85">{p}</span>
                </li>
              </FadeUp>
            ))}
          </ul>
          <p className="mt-8 font-inter text-xs text-hars-muted">{t.warranty.hint}</p>
        </div>
      </div>
    </section>
  );
}
